import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Copy, KeyRound, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { formatTimestamp } from "@/utils/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/auth/platform-owner`;

function messageFor(error, fallback) {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg).filter(Boolean).join("; ") || fallback;
  }
  return fallback;
}

export default function PlatformSecurityPanel() {
  const [status, setStatus] = useState({ remaining_codes: 0, generated_at: null });
  const [loading, setLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [codes, setCodes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/recovery-codes/status`);
      setStatus(response.data);
    } catch (requestError) {
      setError(messageFor(requestError, "Recovery-code status could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const generate = async (event) => {
    event.preventDefault();
    setError("");
    if (status.remaining_codes > 0) {
      const confirmed = window.confirm(
        "Replace your existing recovery codes? Every older unused code will stop working.",
      );
      if (!confirmed) return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/recovery-codes`, {
        current_password: currentPassword,
      });
      setCodes(response.data.recovery_codes || []);
      setStatus({
        remaining_codes: response.data.remaining_codes,
        generated_at: response.data.generated_at,
      });
      setCurrentPassword("");
      toast.success("New recovery codes created. Save them now; they are shown only once.");
    } catch (requestError) {
      const message = messageFor(requestError, "Recovery codes could not be created.");
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.success("Recovery codes copied.");
    } catch {
      toast.error("Copy failed. Select and save the codes manually.");
    }
  };

  return (
    <section data-testid="platform-security-panel" style={{ maxWidth: "760px" }}>
      <div
        style={{
          border: "1px solid #dbe2ea",
          borderRadius: "14px",
          padding: "1.1rem",
          background: "#fff",
        }}
      >
        <div style={{ display: "flex", gap: "0.65rem", alignItems: "center" }}>
          <ShieldCheck aria-hidden="true" size={20} />
          <div>
            <p className="operations-kicker" style={{ margin: 0 }}>Platform Owner security</p>
            <h2 style={{ margin: "0.15rem 0" }}>Emergency recovery codes</h2>
          </div>
        </div>
        <p style={{ lineHeight: 1.55 }}>
          Keep these codes somewhere separate from JFM. If you forget your Platform Owner
          password, one code can authorize a password reset. A successful recovery signs out
          every existing Platform Owner session and disables the rest of that code set.
        </p>

        <div
          data-testid="platform-recovery-code-status"
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            padding: "0.85rem 1rem",
            marginBottom: "1rem",
          }}
        >
          {loading ? (
            <span>Checking recovery-code status…</span>
          ) : (
            <>
              <strong>{status.remaining_codes} unused recovery code{status.remaining_codes === 1 ? "" : "s"}</strong>
              {status.generated_at && (
                <div style={{ color: "#64748b", fontSize: "0.82rem", marginTop: "0.2rem" }}>
                  Current set created {formatTimestamp(status.generated_at)}
                </div>
              )}
            </>
          )}
        </div>

        <form data-testid="platform-recovery-code-generate-form" onSubmit={generate}>
          <label htmlFor="platform-recovery-current-password">
            Confirm your current Platform Owner password
            <input
              autoComplete="current-password"
              data-testid="platform-recovery-current-password-input"
              id="platform-recovery-current-password"
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
              type="password"
              value={currentPassword}
            />
          </label>
          {error && <p data-testid="platform-recovery-code-error" role="alert">{error}</p>}
          <button
            data-testid="platform-recovery-code-generate-button"
            disabled={submitting || !currentPassword}
            type="submit"
          >
            {status.remaining_codes > 0 ? <RefreshCw size={16} /> : <KeyRound size={16} />}
            {submitting
              ? "Creating secure codes…"
              : status.remaining_codes > 0
                ? "Replace recovery codes"
                : "Create recovery codes"}
          </button>
        </form>

        {codes.length > 0 && (
          <div
            data-testid="platform-recovery-codes-once"
            style={{
              marginTop: "1.1rem",
              border: "1px solid #f59e0b",
              borderRadius: "12px",
              padding: "1rem",
              background: "#fffbeb",
            }}
          >
            <strong>Save these now — JFM will not show this set again.</strong>
            <p style={{ margin: "0.35rem 0 0.8rem", lineHeight: 1.45 }}>
              Store one copy in your password manager and, ideally, one printed copy in a secure
              place. Do not keep the only copy inside JFM.
            </p>
            <div
              style={{
                display: "grid",
                gap: "0.35rem",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: "0.92rem",
              }}
            >
              {codes.map((code, index) => (
                <code data-testid={`platform-recovery-code-${index}`} key={code}>
                  {index + 1}. {code}
                </code>
              ))}
            </div>
            <button
              data-testid="platform-recovery-code-copy-button"
              onClick={copyCodes}
              style={{ marginTop: "0.85rem" }}
              type="button"
            >
              <Copy size={16} />
              Copy all codes
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
